import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { captureException, createErrorHandler, setTag } from './sentry-lazy';
import { SessionService } from '@sites/util-storage';
import { ErrorRedirect } from './error-redirect';

export const CHUNK_RELOAD_KEY = 'chunk-reload-attempted';

@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  private router = inject(Router);
  private sessionService = inject(SessionService);
  private destroyRef = inject(DestroyRef);

  private sentryErrorHandler: { handleError(error: unknown): void };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private errors: any[] = [];
  private pendingNavigationUrl: string | null = null;

  constructor() {
    this.sentryErrorHandler = createErrorHandler({
      extractor: this.extractor.bind(this),
    });

    // Track navigation target URL for chunk load error handling
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.pendingNavigationUrl = event.url;
        } else if (event instanceof NavigationEnd) {
          // Navigation succeeded - clear the reload flag so future errors can trigger reload
          this.clearChunkReloadFlag();
          this.pendingNavigationUrl = null;
        } else if (event instanceof NavigationCancel) {
          this.pendingNavigationUrl = null;
        }
        // Note: NavigationError doesn't clear pendingNavigationUrl - we need it for error handling
      });
  }

  /**
   * Clear the chunk reload flag. Call this when the app initializes
   * successfully to reset the reload prevention mechanism.
   */
  clearChunkReloadFlag(): void {
    this.sessionService.remove(CHUNK_RELOAD_KEY);
  }

  /**
   * Handle all errors raised in the application
   * @param error
   */
  handleError(error: unknown) {
    // Check for chunk load error first - handle with automatic reload
    if (this.isChunkLoadError(error)) {
      this.handleChunkLoadError(error);
      return;
    }

    // Send the error to Sentry for logging
    this.sentryErrorHandler.handleError(error);

    if (error instanceof ErrorRedirect) {
      this.router.navigate(error.commands, error.extras);
    }

    this.errors.push(error);
  }

  /**
   * Check if the error is a chunk load error (lazy loading failure)
   * @param error The error to check
   * @returns True if this is a chunk load error
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private isChunkLoadError(error: any): boolean {
    const message = error?.message || '';
    const name = error?.name || '';
    return (
      name === 'ChunkLoadError' ||
      // Webpack pattern
      /Loading chunk [\w.-]+ failed/.test(message) ||
      /ChunkLoadError/.test(message) ||
      // Vite/esbuild pattern
      /(.*)dynamically imported module/.test(message)
    );
  }

  /**
   * Handle chunk load errors by reloading the page once.
   * Uses SessionService to prevent infinite reload loops.
   * @param error The chunk load error
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleChunkLoadError(error: any): void {
    const reloadAttempted = this.sessionService.read(CHUNK_RELOAD_KEY);

    if (!reloadAttempted) {
      // First attempt: set flag and reload to get fresh chunks
      this.sessionService.write(CHUNK_RELOAD_KEY, 'true');

      if (this.pendingNavigationUrl) {
        // Navigate to the intended URL (forces full page load with fresh chunks)
        window.location.href = this.pendingNavigationUrl;
      } else {
        // Fallback: reload current page if we can't determine target
        window.location.reload();
      }
    } else {
      // Already tried once - clear flag, log to Sentry, don't reload again
      this.sessionService.remove(CHUNK_RELOAD_KEY);
      this.sentryErrorHandler.handleError(error);
      this.errors.push(error);
    }
  }

  /**
   * Capture error in our logs
   * @param error
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  captureError(error: any) {
    // Send the error to Sentry
    captureException(error);

    this.errors.push(error);
  }

  /**
   * Try to find out what the last error message was
   * @returns Message
   */
  getLastMessage(): string {
    if (this.errors.length === 0) {
      return '';
    }

    return this.errors[this.errors.length - 1].message || 'Unknown error';
  }

  /**
   * Helper to tag all error events
   * @param name Name of the tag
   * @param value Value of the tag
   */
  addTag(name: string, value: string) {
    setTag(name, value);
  }

  /**
   * Extract our internal errors for better display on Sentry
   * @param error
   * @param defaultExtractor
   * @returns unknown
   */
  private extractor(
    error: unknown,
    defaultExtractor: (error: unknown) => unknown
  ) {
    // Use default extractor for everything else
    return defaultExtractor(error);
  }
}
